import type * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'

import { State } from './state'

/**
 * Core actions for space lite record functional usage.
 */
// NOTE: intentionally missing return type so that type can be inferred
export function getActionsConfig() {
  return {
    /**
     * Loads functional usage for the current space record and scenario context.
     * Resolves both from the collection in the effect.
     */
    loadForCurrentEntity: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.NONE,
      reducers: {
        request: () => ({
          ...State.INITIAL,
          isLoading: true,
        }),
        error: ({ state, data: { error } }) => State.onError(error, state),
        cancellation: ({ state }) => State.onCancellation(state),
      },
    }),
    /** Loads functional usage for the supplied space record and scenario context */
    load: store.actions.request({
      input: store.actions.Data.asIs<{
        context: ents.core.admin.Context.ForScenario
        spaceRecordId: ents.extra.space.Record.Id
      }>(),
      output: store.actions.Data.asIs<State.Methodology | undefined>(),
      reducers: {
        request: ({ state }) => State.onRequest(state),
        success: ({ state, data: { response } }) =>
          State.onSuccess(response, state),
        error: ({ state, data: { error } }) => State.onError(error, state),
        cancellation: ({ state }) => State.onCancellation(state),
      },
    }),
    /** Sets the methodology to apply in the editing state */
    setMethodology: store.actions.standalone({
      data: store.actions.Data.asIs<State.Methodology>(),
      reducer: ({ state, data: editedMethodology }) => ({
        ...state,
        editedMethodology,
      }),
    }),
    /** Discard the editing state */
    discard: store.actions.standalone({
      data: store.actions.Data.NONE,
      reducer: ({ state }) => ({
        ...state,
        editedMethodology: undefined,
      }),
    }),
    /**
     * Submits the edited methodology.
     *
     * Cancelled by its effect when the methodology didn't actually change.
     */
    submit: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.asIs<State.Methodology | undefined>(),
      reducers: {
        request: ({ state }) => State.onRequest(state),
        success: ({ state, data: { response } }) => ({
          ...State.onSuccess(response, state),
          editedMethodology: undefined,
        }),
        error: ({ state, data: { error } }) => State.onError(error, state),
        cancellation: ({ state }) => State.onCancellation(state),
      },
    }),
  } as const satisfies store.actions.Config.Constraint<State>
}
